// 004 (FR-003, FR-035–FR-037, FR-053; P-PROF-3, BR-1): Delivery addresses. Each row shows its title,
// the Default badge and one line; Edit opens the form, Set as default and Delete (after
// confirmation) re-list from the server, which picks the new default.
import { useRouter } from "expo-router";
import { useState } from "react";
import { View } from "react-native";

import { isAppError } from "@/api/errors";
import { ConfirmDialog } from "@/ui/ConfirmDialog";
import { InlineMessage } from "@/ui/InlineMessage";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

import { PROFILE_COPY, profileErrorMessage } from "./copy";
import { Card, SectionHead, useProfileStyles } from "./ui";
import { useParentData } from "./useParentData";
import { type AddressVM, canAddAddress } from "./viewModels";

export function AddressesSection({
  items,
  online,
}: {
  items: readonly AddressVM[];
  online: boolean;
}) {
  const styles = useProfileStyles();
  const router = useRouter();
  const { actions } = useParentData();
  const [toDelete, setToDelete] = useState<AddressVM | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canAdd = canAddAddress(items.length);

  async function run(work: () => Promise<unknown>) {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await work();
    } catch (e) {
      if (isAppError(e))
        setError(profileErrorMessage(e, { action: "address" }).form ?? null);
    } finally {
      setPending(false);
    }
  }

  return (
    <Card testID="profile-addresses">
      <SectionHead
        title={PROFILE_COPY.addresses}
        subtitle={PROFILE_COPY.addressesSubtitle}
        action={
          canAdd ? (
            <PressableTarget
              disabled={!online}
              onPress={() => router.push("/addresses/new")}
            >
              {PROFILE_COPY.addAddress}
            </PressableTarget>
          ) : null
        }
      />
      {!canAdd ? <Text tone="muted">{PROFILE_COPY.addressLimit}</Text> : null}
      {items.length === 0 ? (
        <Text tone="muted">{PROFILE_COPY.addressesEmpty}</Text>
      ) : null}
      {error ? <InlineMessage tone="error">{error}</InlineMessage> : null}
      {items.map((a) => (
        <View key={a.id} style={styles.row} testID={`address-${a.id}`}>
          <View style={styles.rowText}>
            <View style={styles.actions}>
              <Text weight="semiBold">{a.title}</Text>
              {a.isDefault ? (
                <View style={styles.badge}>
                  <Text size="xs" weight="semiBold" style={styles.badgeText}>
                    {PROFILE_COPY.defaultBadge}
                  </Text>
                </View>
              ) : null}
            </View>
            <Text tone="muted">{a.oneLine}</Text>
          </View>
          <View style={styles.actions}>
            {!a.isDefault ? (
              <PressableTarget
                accessibilityLabel={`${PROFILE_COPY.setDefault}: ${a.title}`}
                disabled={!online || pending}
                onPress={() => void run(() => actions.setDefaultAddress(a.id))}
              >
                <Text weight="semiBold">{PROFILE_COPY.setDefault}</Text>
              </PressableTarget>
            ) : null}
            <PressableTarget
              accessibilityLabel={PROFILE_COPY.editAddress(a.title)}
              disabled={!online}
              onPress={() => router.push(`/addresses/${a.id}`)}
            >
              <Text weight="semiBold">Edit</Text>
            </PressableTarget>
            <PressableTarget
              accessibilityLabel={PROFILE_COPY.deleteAddress(a.title)}
              disabled={!online || pending}
              onPress={() => setToDelete(a)}
            >
              <Text weight="semiBold">{PROFILE_COPY.delete}</Text>
            </PressableTarget>
          </View>
        </View>
      ))}
      <ConfirmDialog
        visible={toDelete !== null}
        title={PROFILE_COPY.deleteConfirm}
        message={toDelete?.oneLine}
        confirmLabel={PROFILE_COPY.delete}
        cancelLabel={PROFILE_COPY.cancel}
        pending={pending}
        onCancel={() => setToDelete(null)}
        onConfirm={() => {
          const target = toDelete;
          if (!target) return;
          void run(() => actions.deleteAddress(target.id)).then(() =>
            setToDelete(null),
          );
        }}
      />
    </Card>
  );
}
